<script setup lang="ts">
import type {
  NewProduct,
  NewProductModification,
} from '@/types/api/api-v4/product'
import {
  ProductDetailType,
  ProductParentType,
} from '@/types/components/product-card.types'

import ProductDetailComponents from '@/components/Product/ProductDetail/ProductDetailComponents.vue'
import ProductDetailComponentsSlider from '@/components/Product/ProductDetail/ProductDetailComponentsSlider.vue'
import ProductDetailDescription from '@/components/Product/ProductDetail/ProductDetailDescription.vue'
import ProductDetailImage from '@/components/Product/ProductDetail/ProductDetailImage.vue'
import ProductDetailImageTags from '@/components/Product/ProductDetail/ProductDetailImageTags.vue'
import ProductDetailModifications from '@/components/Product/ProductDetail/ProductDetailModifications.vue'
import ProductDetailPricePanel from '@/components/Product/ProductDetail/ProductDetailPricePanel.vue'
import ProductDetailTitle from '@/components/Product/ProductDetail/ProductDetailTitle.vue'
import ProductTags from '@/components/Product/ProductTags/ProductTags.vue'

const MODIFICATION_COUNT = 2

type Props = {
  product: NewProduct
  type: ProductDetailType
  currentModification: NewProductModification
}
const props = defineProps<Props>()

const emit = defineEmits(['update:current-modification'])

const { isMobile } = useResponsive()

const schemaAttributes = computed(() => {
  if (props.type !== ProductDetailType.Page) {
    return {
      itemScope: undefined,
      itemType: undefined,
    }
  }

  return {
    itemScope: true,
    itemType: 'http://schema.org/Product',
  }
})

const onUpdateCurrentModification = (modificationId: number) => {
  emit('update:current-modification', modificationId)
}
</script>

<template>
  <div
    class="product-detail-card"
    :itemscope="schemaAttributes.itemScope"
    :itemtype="schemaAttributes.itemType"
  >
    <div class="product-detail-card__left-block">
      <product-detail-image-tags
        class="product-detail-card__image-tags"
        :product="product"
        :current-modification="currentModification"
      />

      <product-detail-image
        :product="product"
        :current-modification="currentModification"
        :type="type"
      />
    </div>

    <div class="product-detail-card__right-block">
      <product-tags
        v-if="isMobile"
        :main-tags="product.main_tags"
        :additional-tags="product.additional_tags"
        :current-modification="currentModification"
        :type="ProductParentType.detail"
        class="product-detail-card__tags"
      />

      <product-detail-title
        :product="product"
        :current-modification="currentModification"
        class="product-detail-card__title"
      />

      <product-detail-description
        :product="product"
        :current-modification="currentModification"
      />

      <product-detail-modifications
        v-if="product.modifications.length >= MODIFICATION_COUNT"
        class="product-detail-card__modifications"
        :modifications="product.modifications"
        @change-modification="onUpdateCurrentModification"
      />

      <product-detail-price-panel
        v-if="!isMobile"
        class="product-detail-card__price-panel"
        :current-modification="currentModification"
      />
    </div>

    <product-detail-components
      v-if="product.components.length > 0 && isMobile"
      class="product-detail-card__components"
      :components="product.components"
    />
  </div>

  <product-detail-components-slider
    v-if="product.components.length > 0 && !isMobile"
    :components="product.components"
    class="product-detail-card__components"
  />
</template>

<style scoped lang="scss">
.product-detail-card {
  position: relative;

  @include media('>=tablet') {
    display: flex;
    gap: 30px;
    align-items: center;
  }

  &__image-tags {
    position: absolute;
    top: -12px;
    overflow: hidden;

    @include media('>=tablet') {
      position: relative;
      top: unset;
      margin-bottom: 16px;
    }
  }

  &__tags {
    margin: 16px 0 8px;
    overflow: hidden;
  }

  &__title {
    margin-bottom: 4px;

    @include media('>=tablet') {
      margin-bottom: 8px;
    }
  }

  &__modifications {
    margin-top: 24px;
  }

  :deep(.product-detail-card__price-panel) {
    margin-top: 24px;
  }

  &__components {
    margin-top: 24px;

    @include media('>=tablet') {
      margin-top: 30px;
    }
  }

  &__left-block {
    @include media('>=tablet') {
      flex-basis: 426px;
      max-width: 426px;
    }
  }

  &__right-block {
    @include media('>=tablet') {
      flex-shrink: 0;
      flex-basis: 273px;
      max-width: 273px;
    }
  }
}
</style>
